import React from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Flex, Label } from 'components';
import { H4, P } from 'components/typography';

import { TaskDetailsViewModel } from '../../../hooks/buildTaskDetailsViewModel';
import styles from '../index.scss';

type TaskDetailsSummaryProps = {
  viewModel: TaskDetailsViewModel;
};

export function TaskDetailsSummary({ viewModel }: TaskDetailsSummaryProps) {
  const { t } = useTranslation();

  return (
    <Flex direction="row" width="full">
      <Flex
        className={styles.TaskDetailsBlock}
        width="full"
        direction="column"
        gap={4}
      >
        <H4 mt={2}>
          {t('web.admin.workOrder.preventiveMaintenance.task.details.heading')}
        </H4>
        {viewModel.serviceRequestLinks.length > 0 && (
          <div className={styles.serviceRequestContainer}>
            <Label className={styles.detailsText}>
              {t(
                'web.admin.workOrder.preventiveMaintenance.task.details.serviceRequest'
              )}
            </Label>
            {viewModel.serviceRequestLinks.map(serviceRequestLink => (
              <Link
                key={serviceRequestLink.id}
                data-test={`pm-task-service-request-link-${serviceRequestLink.userFriendlyId}`}
                to={serviceRequestLink.path}
                className={styles.serviceRequestLink}
              >
                {serviceRequestLink.userFriendlyId}
              </Link>
            ))}
          </div>
        )}
        <Box className={styles.TaskDetailsScheduleGrid}>
          <Flex direction="column">
            <Label className={styles.detailsText}>
              {t`web.admin.workOrder.preventiveMaintenance.task.details.schedule`}
            </Label>
            {viewModel.schedule &&
              (viewModel.schedule.path ? (
                <Link to={viewModel.schedule.path}>
                  {viewModel.schedule.title}
                </Link>
              ) : (
                <P>{viewModel.schedule.title}</P>
              ))}
          </Flex>
          {viewModel.shouldDisplayLocation && (
            <Flex direction="column">
              <Label className={styles.detailsText}>
                {t`web.admin.workOrder.preventiveMaintenance.task.details.equipment.location`}
              </Label>
              <P mb={2}>{viewModel.location}</P>
            </Flex>
          )}
          <Flex direction="column">
            <Label className={styles.detailsText}>
              {t`web.admin.workOrder.preventiveMaintenance.task.details.repeats`}
            </Label>
            <P>{viewModel.repeatsLabel}</P>
          </Flex>
        </Box>
        <Box className={styles.TaskDetailsScheduleGrid}>
          <Flex direction="column">
            <Label className={styles.detailsText}>
              {t`web.admin.workOrder.preventiveMaintenance.task.details.timeToComplete`}
            </Label>
            <P>
              {viewModel.estimatedHours}{' '}
              {t`web.admin.workOrder.preventiveMaintenance.task.details.hoursUnit`}
            </P>
          </Flex>

          <Flex direction="column">
            <Label className={styles.detailsText}>
              {t`web.admin.workOrder.preventiveMaintenance.task.details.dueDate`}
            </Label>
            <P>{viewModel.dueDate}</P>
          </Flex>
          <Flex direction="column">
            <Label className={styles.detailsText}>
              {t`web.admin.workOrder.preventiveMaintenance.task.details.completedDate`}
            </Label>
            <P> {viewModel.completedDate}</P>
          </Flex>
        </Box>

        <Flex direction="column">
          <Label className={styles.detailsText}>
            {t`web.admin.workOrder.preventiveMaintenance.task.details.scheduleNotes`}
          </Label>
          <P className={styles.multiLineText} mb={2}>
            {viewModel.scheduleNotes}
          </P>
        </Flex>
      </Flex>
    </Flex>
  );
}
